import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBell,
  FaCheckDouble,
  FaExternalLinkAlt,
  FaShoppingBag,
  FaSyncAlt,
} from "react-icons/fa";
import sellerApi from "../../utils/sellerApi";

const formatBadgeCount = (count) => (count > 99 ? "99+" : String(count));

const formatRelativeTime = (value) => {
  if (!value) return "";

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "";

  const diffSeconds = Math.max(Math.floor((Date.now() - timestamp) / 1000), 0);
  if (diffSeconds < 60) return "Just now";
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(value).toLocaleDateString();
};

function SellerNotificationBell() {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [markingAll, setMarkingAll] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await sellerApi.get("seller/notifications/unread-count");
      setUnreadCount(Number(response.data?.unreadCount || 0));
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const fetchLatest = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await sellerApi.get("seller/notifications", {
        params: { page: 1, limit: 8, status: "all" },
      });
      const data = response.data?.data || {};
      setNotifications(data.notifications || []);
      setUnreadCount(Number(data.unreadCount || 0));
    } catch {
      setError("Unable to load notifications. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchUnreadCount();
    const intervalId = window.setInterval(fetchUnreadCount, 45000);
    const handleFocus = () => void fetchUnreadCount();
    const handleVisibilityChange = () => {
      if (!document.hidden) void fetchUnreadCount();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOpen = () => {
    setOpen((current) => {
      const nextOpen = !current;
      if (nextOpen) {
        void fetchLatest();
      }
      return nextOpen;
    });
  };

  const markAsRead = async (notification) => {
    if (notification.isRead) return;

    try {
      const response = await sellerApi.patch(
        `seller/notifications/${notification.id}/read`
      );
      setUnreadCount(Number(response.data?.unreadCount || 0));
      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id ? { ...item, isRead: true } : item
        )
      );
    } catch {
      await fetchUnreadCount();
    }
  };

  const openNotification = async (notification) => {
    await markAsRead(notification);
    setOpen(false);
    navigate(notification.actionUrl || "/seller/order");
  };

  const markAllAsRead = async () => {
    try {
      setMarkingAll(true);
      await sellerApi.patch("seller/notifications/read-all");
      setUnreadCount(0);
      setNotifications((current) =>
        current.map((notification) => ({ ...notification, isRead: true }))
      );
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="seller-notifications" ref={dropdownRef}>
      <button
        type="button"
        className="seller-notification-button"
        onClick={toggleOpen}
        aria-label="Notifications"
      >
        <FaBell className="bell-icon" />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {formatBadgeCount(unreadCount)}
          </span>
        )}
      </button>

      {open && (
        <div className="seller-notification-dropdown">
          <div className="seller-notification-dropdown-header">
            <div>
              <h3>Notifications</h3>
              {unreadCount > 0 && (
                <span>{formatBadgeCount(unreadCount)} unread</span>
              )}
            </div>
            <button
              type="button"
              onClick={markAllAsRead}
              disabled={!unreadCount || markingAll}
            >
              <FaCheckDouble />
              Mark all
            </button>
          </div>

          <div className="seller-notification-dropdown-body">
            {loading ? (
              <div className="seller-notification-loading">
                <span />
                <span />
                <span />
              </div>
            ) : error ? (
              <div className="seller-notification-error">
                <p>{error}</p>
                <button type="button" onClick={fetchLatest}>
                  <FaSyncAlt />
                  Retry
                </button>
              </div>
            ) : notifications.length ? (
              notifications.map((notification) => (
                <button
                  type="button"
                  className={`seller-notification-item ${
                    notification.isRead ? "" : "unread"
                  }`}
                  key={notification.id}
                  onClick={() => void openNotification(notification)}
                >
                  <FaShoppingBag className="seller-notification-item-icon" />
                  <span className="seller-notification-item-content">
                    <strong>{notification.title}</strong>
                    <small>{notification.message}</small>
                    <em>{formatRelativeTime(notification.createdAt)}</em>
                  </span>
                  <FaExternalLinkAlt className="seller-notification-link-icon" />
                </button>
              ))
            ) : (
              <div className="seller-notification-empty">
                No notifications found.
              </div>
            )}
          </div>

          <button
            type="button"
            className="seller-notification-view-all"
            onClick={() => {
              setOpen(false);
              navigate("/seller/notifications");
            }}
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}

export default SellerNotificationBell;
